import { useEffect, useRef, useState, type CSSProperties } from 'react';

export const TEDDY_STATES = [
  'idle', 'hello', 'listening', 'thinking', 'happy', 'wink', 'speaking',
] as const;
export type TeddyState = (typeof TEDDY_STATES)[number];
export const TEDDY_DURATION_MS: Record<TeddyState, number> = {
  idle: 4000, hello: 3000, listening: 4000, thinking: 3600,
  happy: 2800, wink: 3200, speaking: 2400,
};

export interface TeddyBotProps {
  /** Driven by the real application state, not by timers pretending to do work. */
  state?: TeddyState;
  /** CSS pixels. 96px uses the 192px asset for a sharp compact mascot. */
  size?: number;
  /** Directory containing assets/ and posters/. Treat as trusted app config. */
  assetBase?: string;
  /** Explicit pause. System reduced-motion settings always take precedence. */
  animate?: boolean;
  /** Empty by default because a chat-launcher button should supply its own label. */
  alt?: string;
  className?: string;
}

const EMPTY_IMAGE = 'data:image/gif;base64,R0lGODlhAQABAAD/ACwAAAAAAQABAAACADs=';

/**
 * Dependency-free (apart from React) animated-image integration.
 * Uses a static poster when reduced motion is requested, the page is hidden,
 * the mascot is offscreen, or animate=false. Only the active state is loaded.
 * For deterministic frame control or one-shot playback, use the sprite atlas.
 */
export function TeddyBot({
  state = 'idle', size = 96, assetBase = '/teddy', animate = true,
  alt = '', className,
}: TeddyBotProps) {
  const hostRef = useRef<HTMLSpanElement>(null);
  // Begin with the poster, including during SSR/hydration.
  const [reducedMotion, setReducedMotion] = useState(true);
  const [pageVisible, setPageVisible] = useState(true);
  const [inView, setInView] = useState(false);
  const [failedSources, setFailedSources] = useState<ReadonlySet<string>>(new Set());

  useEffect(() => {
    const media = window.matchMedia('(prefers-reduced-motion: reduce)');
    const syncMotion = () => setReducedMotion(media.matches);
    const syncVisibility = () => setPageVisible(document.visibilityState !== 'hidden');
    syncMotion();
    syncVisibility();
    media.addEventListener('change', syncMotion);
    document.addEventListener('visibilitychange', syncVisibility);
    return () => {
      media.removeEventListener('change', syncMotion);
      document.removeEventListener('visibilitychange', syncVisibility);
    };
  }, []);

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;
    if (typeof IntersectionObserver === 'undefined') {
      setInView(true);
      return;
    }
    const observer = new IntersectionObserver(
      ([entry]) => setInView(entry?.isIntersecting ?? true), { threshold: 0 },
    );
    observer.observe(host);
    return () => observer.disconnect();
  }, []);

  const safeState: TeddyState = TEDDY_STATES.includes(state) ? state : 'idle';
  const cssSize = Number.isFinite(size) ? Math.min(192, Math.max(32, size)) : 96;
  const pixels = cssSize <= 64 ? 128 : cssSize <= 96 ? 192 : 256;
  const base = assetBase.replace(/\/+$/, '');
  const motionAllowed = animate && !reducedMotion && pageVisible && inView;
  const animated = `${base}/assets/teddy-${safeState}-${pixels}.webp`;
  const poster = `${base}/posters/teddy-${safeState}-${pixels}.png`;
  const fallback = `${base}/posters/teddy-idle-${pixels}.png`;
  const candidates = motionAllowed ? [animated, poster, fallback] : [poster, fallback];
  const src = candidates.find(candidate => !failedSources.has(candidate)) ?? EMPTY_IMAGE;

  const hostStyle: CSSProperties = {
    display: 'inline-block', width: cssSize, height: cssSize,
    lineHeight: 0, verticalAlign: 'middle', flexShrink: 0,
  };
  const imageStyle: CSSProperties = {
    display: 'block', width: '100%', height: '100%',
    objectFit: 'contain', userSelect: 'none',
  };

  return (
    <span
      ref={hostRef}
      className={className}
      style={hostStyle}
      data-teddy-state={safeState}
      data-teddy-motion={src === animated ? 'playing' : 'paused'}
    >
      <img
        src={src}
        width={cssSize}
        height={cssSize}
        alt={alt}
        aria-hidden={alt ? undefined : true}
        decoding="async"
        draggable={false}
        style={imageStyle}
        onError={() => {
          if (src !== EMPTY_IMAGE) {
            setFailedSources(previous => new Set([...previous, src]));
          }
        }}
      />
    </span>
  );
}

export default TeddyBot;
